"use client";
import React, { useEffect, useState } from 'react';
import { useSession } from 'next-auth/react';
import { useLanguage } from '@/context/LanguageContext';

export default function WelcomePopup() {
  const { t } = useLanguage();
  const [show, setShow] = useState(false);
  const { data: session, status } = useSession();
  const [userName, setUserName] = useState<string>('');
  const [userRole, setUserRole] = useState<string | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    if (status === 'loading') return;

    const wantsToShow = sessionStorage.getItem('showWelcome') === 'true';
    if (!wantsToShow) return;

    const isWPLoggedIn = localStorage.getItem('isLoggedIn') === 'true';

    // If they cancelled the login or it failed, remove the flag and don't show
    if (status === 'unauthenticated' && !isWPLoggedIn) {
      sessionStorage.removeItem('showWelcome');
      return;
    }

    // Only show if they are actually authenticated
    if (status === 'authenticated' || isWPLoggedIn) {
      setShow(true);
      sessionStorage.removeItem('showWelcome');
      
      let nameToDisplay = '';
      if (session?.user?.name) {
        nameToDisplay = session.user.name;
      } else if (session?.user?.email) {
        nameToDisplay = session.user.email;
      } else {
        const localEmail = localStorage.getItem('userEmail');
        nameToDisplay = localEmail || '';
      }
      
      setUserName(nameToDisplay);
      
      // Role is fetched asynchronously by Navbar after login, so we wait for it
      const intervalId = setInterval(() => {
        const role = localStorage.getItem('userRole');
        if (role) {
          setUserRole(role);
          clearInterval(intervalId);
        }
      }, 200);

      setTimeout(() => {
        clearInterval(intervalId);
        setShow(false);
      }, 5000);
    }
  }, [session, status]);

  if (!show) return null;

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999, backgroundColor: 'rgba(0,0,0,0.2)', backdropFilter: 'blur(4px)', animation: 'fadeIn 0.3s ease-out' }}>
      <div style={{ backgroundColor: 'white', padding: '2.5rem 3rem', borderRadius: '1rem', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)', textAlign: 'center', transform: 'translateY(0)', animation: 'slideUp 0.4s ease-out', maxWidth: '90%', width: '400px' }}>
        <div style={{ width: '72px', height: '72px', backgroundColor: '#ecfdf5', borderRadius: '50%', display: 'flex', justifyContent: 'center', alignItems: 'center', margin: '0 auto 1.5rem auto' }}>
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
        </div>
        <h2 style={{ fontSize: '1.75rem', fontWeight: 700, color: '#111827', marginBottom: '0.75rem' }}>{t('Welcome!')}</h2>
        <p style={{ fontSize: '1.125rem', color: '#4b5563', lineHeight: 1.5, wordBreak: 'break-word', marginBottom: userRole === 'company' ? '1rem' : '0' }}>
          {t('Glad to have you here')}{userName ? ',' : ''}
          {userName && (
            <>
              <br />
              <span style={{ fontWeight: 600, color: '#1f2937', marginTop: '0.25rem', display: 'inline-block' }}>{userName}</span>
            </>
          )}
        </p>
        
        {userRole === 'company' && (
          <div style={{ display: 'inline-flex', alignItems: 'center', backgroundColor: '#eff6ff', padding: '0.5rem 1rem', borderRadius: '2rem', border: '1px solid #dbeafe', marginTop: '0.5rem' }}>
            <span style={{ width: '8px', height: '8px', backgroundColor: '#1c7bff', borderRadius: '50%', marginRight: '8px' }}></span>
            <span style={{ color: '#1c7bff', fontWeight: 600, fontSize: '0.9rem' }}>{t('Company account')}</span>
          </div>
        )}
        <button onClick={() => setShow(false)} style={{ marginTop: '2rem', width: '100%', padding: '0.875rem', backgroundColor: '#1c7bff', color: 'white', border: 'none', borderRadius: '0.5rem', fontWeight: 600, fontSize: '1rem', cursor: 'pointer', transition: 'background-color 0.2s', boxShadow: '0 4px 6px -1px rgba(28, 123, 255, 0.2)' }}>
          {t('Continue to Homepage')}
        </button>
      </div>
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
